import {
  LargeImageBackgroundType,
  LargeImageSection,
  LargeImageSectionProps,
  SingleImageFeatureSection,
  SingleImageFeatureSectionProps,
  SingleTeamMember,
  SingleTeamMemberProps,
  useCaseType,
} from '@rimactechnology/rimac-ui-lib';

import {
  FooterQuery,
  FormQuery,
  HeroImageSectionFragment,
  ImageFeatureSectionFragment,
  LargeImageSectionFragment,
  NavigationQuery,
  NewsroomFragment,
  SeoFragment,
  SingleTeamMemberSectionFragment,
} from '@/api';
import { Layout } from '@/components/layout/Layout';
import NewsroomSection from '@/components/newsroom/NewsroomSection';
import { Seo } from '@/components/Seo';
import HeroImageSection from '@/components/shared/HeroImageSection';
import getCdlPublicId from '@/hooks/useCdlPublicId';

type Props = {
  seo?: SeoFragment | null;
  hero?: HeroImageSectionFragment | null;
  imageFeature?: ImageFeatureSectionFragment | null;
  // numbers?: NumbersSectionFragment | null; // for future use
  facilitySection?: ImageFeatureSectionFragment | null;
  singleTeamMemberSection?: SingleTeamMemberSectionFragment | null;
  largeImageSection?: LargeImageSectionFragment | null;
  newsroom?: NewsroomFragment | null;
  formData: FormQuery;
  footerData: FooterQuery;
  headerData: NavigationQuery;
};

export const AboutPage = ({
  hero,
  imageFeature,
  // numbers, // for future use
  facilitySection,
  singleTeamMemberSection,
  largeImageSection,
  newsroom,
  seo,
  formData,
  footerData,
  headerData,
}: Props) => {
  // for future use
  // const numbersProps: NumbersSectionProps = {
  //   title: numbers?.title || '',
  //   cards: (numbers?.cards || []).map((card, index) => ({
  //     index,
  //     title: card?.title || '',
  //     value: card?.value || '',
  //     description: card?.description || '',
  //   })),
  // };

  const imageFeatureProps: SingleImageFeatureSectionProps = {
    title: imageFeature?.title || '',
    useCase: 'energy' as useCaseType,
    text: imageFeature?.richText || '',
    image: {
      imgSrc: getCdlPublicId(
        imageFeature?.image?.[0]?.img_desktop?.data?.attributes?.url || '',
      ),
      imgSrcMobile: getCdlPublicId(
        imageFeature?.image?.[0]?.img_mobile?.data?.attributes?.url ||
          undefined,
      ),
      alt: imageFeature?.image?.[0]?.alt || '',
      blurHash:
        imageFeature?.image?.[0]?.img_desktop?.data?.attributes?.blurhash ||
        'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
    },
  };

  const facilitySectionProps: SingleImageFeatureSectionProps = {
    title: facilitySection?.title || '',
    useCase: 'energy' as useCaseType,
    image: {
      imgSrc: getCdlPublicId(
        facilitySection?.image?.[0]?.img_desktop?.data?.attributes?.url || '',
      ),
      imgSrcMobile: getCdlPublicId(
        facilitySection?.image?.[0]?.img_mobile?.data?.attributes?.url ||
          undefined,
      ),
      alt: facilitySection?.image?.[0]?.alt || '',
      blurHash:
        facilitySection?.image?.[0]?.img_desktop?.data?.attributes?.blurhash ||
        'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
    },
  };

  const singleTeamMemberSectionProps: SingleTeamMemberProps = {
    heading: singleTeamMemberSection?.heading || '',
    positionText: singleTeamMemberSection?.positionText || '',
    text: singleTeamMemberSection?.text || '',
    image: {
      imgSrc: getCdlPublicId(
        singleTeamMemberSection?.image?.img_desktop?.data?.attributes?.url ||
          '',
      ),
      imgSrcMobile: getCdlPublicId(
        singleTeamMemberSection?.image?.img_mobile?.data?.attributes?.url ||
          undefined,
      ),
      alt: singleTeamMemberSection?.image?.alt || '',
      blurHash:
        singleTeamMemberSection?.image?.img_desktop?.data?.attributes
          ?.blurhash || 'LAS?DV%M_3D%t7fQofj[~qM{4n-;',
    },
    anchor: singleTeamMemberSection?.anchor || undefined,
  };

  const largeImageSectionProps: LargeImageSectionProps = {
    image: {
      imgSrc: getCdlPublicId(
        largeImageSection?.image?.img_desktop?.data?.attributes?.url || '',
      ),
      imgSrcMobile: getCdlPublicId(
        largeImageSection?.image?.img_mobile?.data?.attributes?.url ||
          undefined,
      ),
      alt: largeImageSection?.image?.alt || '',
      blurHash:
        largeImageSection?.image?.img_desktop?.data?.attributes?.blurhash ||
        '12312312',
    },
    bgColor: 'light-brown' as LargeImageBackgroundType,
    title: largeImageSection?.title || '',
    anchor: largeImageSection?.anchor || undefined,
    description: largeImageSection?.description || '',
    button: {
      useCase: 'energy' as useCaseType,
      label: largeImageSection?.button?.label || '',
      path:
        largeImageSection?.button?.file?.data?.attributes?.url ||
        largeImageSection?.button?.path ||
        '',
      openInNewTab: !!largeImageSection?.button?.openInNewTab,
    },
  };

  return (
    <Layout footerData={footerData} formData={formData} headerData={headerData}>
      <Seo seo={seo} />
      <HeroImageSection hero={hero} subtitleMaxWidth={540} />
      <SingleImageFeatureSection {...imageFeatureProps} textMaxWidth={636} />
      {/*<NumbersSection {...numbersProps} />*/} {/* for future use */}
      <SingleImageFeatureSection {...facilitySectionProps} isLargeImage />
      <SingleTeamMember {...singleTeamMemberSectionProps} />
      <LargeImageSection
        {...largeImageSectionProps}
        descriptionMaxWidth={730}
      />
      <NewsroomSection strapiData={newsroom} />
    </Layout>
  );
};
